/**
 * Pure resolution of a display identity from a Privy user object.
 *
 * Extracted from usePrivySync so the precedence rules are testable without
 * standing up the whole Privy/Supabase/Polygon effect chain.
 *
 * Enabling SMS login (DEV-767) introduced the first users who have no email
 * at all. Every consumer here must therefore treat an empty email as a normal
 * state, not a load-order glitch:
 *   - profiles.email is NOT NULL DEFAULT '' with no unique index, so many
 *     phone-only users writing "" is safe.
 *   - display_name is world-readable via the profiles_select_all policy, so a
 *     phone-derived name is masked down to the last four digits and the raw
 *     E.164 number never leaves this module.
 */

/** The subset of Privy's `User` this module reads. */
export interface PrivyIdentitySource {
  email?: { address?: string } | null;
  google?: { email?: string; name?: string } | null;
  apple?: { email?: string } | null;
  phone?: { number?: string } | null;
  linkedAccounts?: unknown[];
}

/**
 * Resolve the user's email, or "" when they have none.
 *
 * `user.email` / `user.google` may not be populated on the first render while
 * Privy is still loading linked accounts, so `linkedAccounts` is consulted as
 * the authoritative fallback.
 */
export function resolveEmail(user: PrivyIdentitySource): string {
  return (
    user.email?.address ??
    user.google?.email ??
    user.apple?.email ??
    extractEmailFromLinkedAccounts(user.linkedAccounts) ??
    ""
  );
}

/**
 * Reduce a phone number to its last four digits, or null if it has fewer.
 * Punctuation and country-code formatting are ignored.
 */
export function maskPhoneNumber(phone: string | undefined | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : null;
}

/**
 * Resolve a human-facing display name.
 *
 * Precedence: Google profile name, then the local part of whichever email we
 * can resolve, then a masked phone suffix for SMS-only users, then "User".
 * Email wins over phone so that a user who links both keeps the friendlier
 * name.
 */
export function resolveDisplayName(user: PrivyIdentitySource): string {
  const fromEmail = resolveEmail(user).split("@")[0];
  if (fromEmail) return user.google?.name ?? fromEmail;
  if (user.google?.name) return user.google.name;

  const last4 = maskPhoneNumber(user.phone?.number);
  return last4 ? `User ${last4}` : "User";
}

/**
 * Scan Privy's linkedAccounts array for any account with an email.
 * This is more reliable than checking user.google / user.email
 * individually, because those top-level properties may not be
 * populated until linked accounts finish loading.
 */
function extractEmailFromLinkedAccounts(
  linkedAccounts: unknown[] | undefined,
): string | undefined {
  if (!linkedAccounts) return undefined;
  for (const account of linkedAccounts) {
    if (!account || typeof account !== "object") continue;
    const obj = account as Record<string, unknown>;
    // Google OAuth: { type: "google_oauth", email: "..." }
    // Email login: { type: "email", address: "..." }
    // Apple: { type: "apple_oauth", email: "..." }
    // SMS login: { type: "phone", phoneNumber: "..." } — no email, skipped.
    const email =
      (typeof obj.email === "string" ? obj.email : undefined) ??
      (typeof obj.address === "string" ? obj.address : undefined);
    if (email && email.includes("@")) return email;
  }
  return undefined;
}
